"use client";

import { useContext } from "react";
import { UserContext } from "./user-context";
import { DefaultSkeleton2 } from "../skeleton/default-skeleton";
import { RedirectToSignIn, useUser } from "@clerk/nextjs";
import Cookies from "js-cookie";
import { Building2, Mail, ServerCrash, UserRoundX } from "lucide-react";
import { DealershipContext } from "./dealership-context";
import { AuthErrorScreen } from "./auth-error-screen";

export const AuthLayout = ({ children }: { children: React.ReactNode }) => {
  const { isLoading, isAuthed, userError } = useContext(UserContext);
  const { isSignedIn, user: clerkUser } = useUser();
  const { loading: dealershipLoading, dealershipsError } =
    useContext(DealershipContext);

  const clerkEmail = clerkUser?.primaryEmailAddress?.emailAddress;

  if (isLoading) {
    return (
      <div className="h-screen w-full flex justify-center gap-4">
        <DefaultSkeleton2 />
      </div>
    );
  }

  if (!Cookies.get("__session")) {
    <RedirectToSignIn />;
  }

  if (!isAuthed && !isSignedIn) {
    console.log("Redirecting to sign-in");
    return <RedirectToSignIn />;
  }

  // The account signed in to Clerk fine but has no row in CarTracked. Retrying
  // can never fix that, so no retry button - only an administrator can.
  if (userError === "not-found") {
    return (
      <AuthErrorScreen
        tone="warning"
        icon={UserRoundX}
        title="Your account isn't set up yet"
        email={clerkEmail}
      >
        You&apos;re signed in, but there&apos;s no CarTracked account for this
        email address yet. Ask an administrator at your store to finish setting
        up your access.
      </AuthErrorScreen>
    );
  }

  if (userError === "no-email") {
    return (
      <AuthErrorScreen
        tone="warning"
        icon={Mail}
        title="No email address on your sign-in"
      >
        Your sign-in doesn&apos;t have an email address on file, so we
        can&apos;t match it to a CarTracked account. Ask an administrator at
        your store to add one.
      </AuthErrorScreen>
    );
  }

  if (userError === "fetch-failed") {
    return (
      <AuthErrorScreen
        tone="error"
        icon={ServerCrash}
        title="We couldn't load your account"
        retry
      >
        Something went wrong loading your user data. This is usually temporary —
        try again in a moment.
      </AuthErrorScreen>
    );
  }

  // Checked before `dealershipLoading` so a resolved failure wins over the
  // skeleton: the account is signed in and real, but has no store to open.
  if (dealershipsError === "no-access") {
    return (
      <AuthErrorScreen
        tone="warning"
        icon={Building2}
        title="No dealership linked to your account"
        email={clerkEmail}
      >
        Your account hasn&apos;t been linked to a dealership yet, so
        there&apos;s nothing to open. Ask an administrator at your store to
        finish setting up your access.
      </AuthErrorScreen>
    );
  }

  if (dealershipsError === "fetch-failed") {
    return (
      <AuthErrorScreen
        tone="error"
        icon={ServerCrash}
        title="We couldn't load your dealership"
        retry
      >
        Something went wrong loading your dealership. This is usually temporary
        — try again in a moment.
      </AuthErrorScreen>
    );
  }

  if (isAuthed && isSignedIn && dealershipLoading) {
    return (
      <div className="h-screen w-full flex justify-center gap-4">
        <DefaultSkeleton2 />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen w-full">{children}</div>
    </>
  );
};

export default AuthLayout;
